import { defineStore } from 'pinia'
import { omsCsrfHeader } from "../fetch-utils"
import { ref } from "vue"

// localStorage key per order so the operator's bypass confirmation persists
// across reloads and re-opens of the same order, but does NOT bleed into a
// different order. Stays set until the operator explicitly unticks the
// checkbox (untick path goes through setSkipPrizeSetCheck(false) which
// removes the key).
const SKIP_PRIZE_SET_CHECK_STORAGE_PREFIX = 'tc-skip-prize-set-check:'
const skipPrizeSetCheckStorageKey = (orderId) => `${SKIP_PRIZE_SET_CHECK_STORAGE_PREFIX}${orderId}`

export const useTokyoCatchInputPackingStore = defineStore('inputPacking', () => {
  // States
  const states = {
    shipmentReviewId: ref(null),
    orderId: ref(null),
    country: ref(null),
    boxShippingOptions: ref({}),
    boxSizeOptions: ref([]),
    boxes: ref([]),
    skipPrizeSetCheck: ref(false)
  }

  // Computes
  const computes = {}

  // Actions
  const actions = {
    setInputPacking(tokyoCatchOrder) {
      if (!states.shipmentReviewId || !states.orderId) {
        throw new Error('Invalid argument: Required shipmentReviewId and orderId')
      }

      states.country.value = states.boxShippingOptions.value?.country

      states.boxes.value = tokyoCatchOrder.tokyo_catch_order_boxes.map(b => {
        const options = states.boxShippingOptions.value?.[b.id] || { methods: [], selected_id: null, expected_id: null, country: null, fda_entry_required: false }
        return {
          id: b.id,
          boxIndex: b.box_index,
          shippingMethod: b.expected_shipping_method,
          shippingMethodId: options.selected_id,
          expectedShippingMethodId: options.expected_id,
          shippingMethodOptions: options.methods,
          boxSize: b.box_size,
          weight: b.measured_weight_grams || null,
          persistedWeight: b.measured_weight_grams || null,
          country: options.country,
          fda_entry_required: options.fda_entry_required,
          wonPrizes: b.tokyo_catch_won_prizes.map(p => (
            {
              id: p.id,
              title: p.title,
              japaneseTitle: p.japanese_title,
              sku: p.sku,
              grams: p.grams,
              fdaEntryRequired: p.fda_entry_required,
              imageUrl: p.image_url
            }
          ))
        }
      })
    },
    async fetchOrder() {
      const response = await fetch(
        `/tokyo_catch/orders/${states.orderId.value}?with_box_prizes=true`,
        { headers: omsCsrfHeader() }
      )
      const data = await response.json()
      if (data?.error) throw new Error(data.error)
      actions.setInputPacking(data)
      actions.loadSkipPrizeSetCheck()
    },
    loadSkipPrizeSetCheck() {
      if (typeof window === 'undefined' || !states.orderId.value) return
      try {
        states.skipPrizeSetCheck.value =
          window.localStorage.getItem(skipPrizeSetCheckStorageKey(states.orderId.value)) === 'true'
      } catch (e) {
        states.skipPrizeSetCheck.value = false
      }
    },
    setSkipPrizeSetCheck(value) {
      const bool = !!value
      states.skipPrizeSetCheck.value = bool
      if (typeof window === 'undefined' || !states.orderId.value) return
      try {
        const key = skipPrizeSetCheckStorageKey(states.orderId.value)
        if (bool) {
          window.localStorage.setItem(key, 'true')
        } else {
          window.localStorage.removeItem(key)
        }
      } catch (e) {
        // localStorage may be disabled (private mode); state is still set in memory.
      }
    },
    clearSkipPrizeSetCheck() {
      actions.setSkipPrizeSetCheck(false)
    },
    moveItem({ itemId, fromBoxId, toBoxId, targetItemId = null }) {
      const from = states.boxes.value.find(b => b.id === fromBoxId)
      const to = states.boxes.value.find(b => b.id === toBoxId)
      if (!from || !to) return

      // Remove the original item
      const idxFrom = from.wonPrizes.findIndex(i => i.id === itemId)
      if (idxFrom < 0) return
      const [item] = from.wonPrizes.splice(idxFrom, 1)

      // Decide the position
      if (targetItemId) {
        const idxTo = to.wonPrizes.findIndex(i => i.id === targetItemId)
        if (idxTo < 0) {
          to.wonPrizes.push(item)
        } else {
          to.wonPrizes.splice(idxTo, 0, item)
        }
      } else {
        // Default
        to.wonPrizes.push(item)
      }

      // Re-derive each affected box's FDA flag from its current prizes so that
      // client-side eligibility (and the green/red box border) reflects the
      // post-move state without waiting for a server round-trip.
      actions.recomputeFdaFlag(from)
      actions.recomputeFdaFlag(to)
    },
    removeBox(boxId) {
      states.boxes.value = states.boxes.value.filter(b => b.id !== boxId)
    },
    addBox() {
      // Inherit shipping options from the first existing box so the new box has a
      // sane set of carrier dropdown choices. If there is no existing box (shouldn't
      // happen — review always has ≥1 box), fall back to empty methods.
      const reference = states.boxes.value[0] || {}
      const newBox = {
        id: `new-${Date.now()}-${Math.floor(Math.random() * 1e6)}`,
        boxIndex: states.boxes.value.length,
        shippingMethod: '',
        shippingMethodId: null,
        expectedShippingMethodId: reference.expectedShippingMethodId || null,
        shippingMethodOptions: reference.shippingMethodOptions || [],
        boxSize: reference.boxSize || null,
        weight: null,
        country: reference.country || states.country.value,
        fda_entry_required: false,
        wonPrizes: []
      }
      states.boxes.value.push(newBox)
      return true
    },
    removeItem({ boxId, itemId }) {
      const box = states.boxes.value.find(b => b.id === boxId)
      if (!box) return
      box.wonPrizes = box.wonPrizes.filter(i => i.id !== itemId)
      actions.recomputeFdaFlag(box)
    },
    recomputeFdaFlag(box) {
      if (!box) return
      box.fda_entry_required = box.wonPrizes.some(p => p.fdaEntryRequired)
    },
    updateBoxSize({ boxId, size }) {
      const box = states.boxes.value.find(b => b.id === boxId)
      if (box) box.size = size
    },
    updateBoxWeight({ boxId, weight }) {
      const box = states.boxes.value.find(b => b.id === boxId)
      if (box) box.weight = weight
    },
    updateBoxShippingMethod({ boxId, shippingMethodId }) {
      const box = states.boxes.value.find(b => b.id === boxId)
      if (box) box.shippingMethodId = shippingMethodId || null
    },
    async savePacking() {
      if (!states.shipmentReviewId || !states.orderId) {
        throw new Error('Invalid argument: Required shipmentReviewId and orderId')
      }

      const res = await fetch(
        `/tokyo_catch/order_shipment_reviews/${states.shipmentReviewId.value}/input_packing`,
        {
          method: 'PUT',
          headers: omsCsrfHeader(),
          body: JSON.stringify({
            orderId: states.orderId.value,
            boxes: states.boxes.value,
            skipPrizeSetCheck: states.skipPrizeSetCheck.value
          })
        }
      )
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        throw new Error(data.error || 'Failed to save packing')
      }
    }
  }

  return { ...states, ...computes, ...actions }
})
